import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { db, functions } from '@/config/firebase'
import { doc, getDoc, updateDoc, onSnapshot, collection, query, where, getDocs, Timestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { useAuthStore } from './auth'

interface WidgetRule {
  field: string
  operator: string
  value: string
}

interface WidgetConfig {
  is_active: boolean
  visibility: {
    operator: 'AND' | 'OR'
    rules: Array<{
      field: string
      operator: string
      value: string
    }>
  }
  widget_styles: {
    appearance: {
      thread: {
        color: string
        background: string
        font: string
        linkColor: string
      }
      input: {
        color: string
        background: string
        size: string
        highlight: string
        placeholder: {
          text: string
          color: string
        }
      }
      cta: {
        background: string
      }
    }
    promptSuggestions: Array<{ value: string }>
    defaultFont: string
  }
}

interface OrgData {
  id: string
  share_code: string | number
  name?: string
  widget?: WidgetConfig
  subscription?: {
    plan: {
      id: string
      name: string
    }
    status: string
    meter: {
      consumed_units: number
      credited_units: number
      balance: number
    }
    billing: {
      amount: number
      currency: string
      recurring_interval: string
      current_period_start: Date
      current_period_end: Date
      cancel_at_period_end: boolean
      canceled_at?: Date
      started_at: Date
      ends_at?: Date
    }
    customer_portal_url?: string
  }
  keys?: {
    shopify?: {
      storeName: string
      isConnected: boolean
    }
  }
}

interface UserOrg {
  user_id: string
  org_id: string
  updated_at?: Date
}

interface OrgWithName extends UserOrg {
  org_name?: string
}

export const useOrgStore = defineStore('org', () => {
  const orgData = ref<OrgData | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const unsubscribe = ref<(() => void) | null>(null)
  const searchCount = ref<number>(0)
  const searchLimit = ref<number>(500) // 1K searches per month
  const userOrgs = ref<OrgWithName[]>([])
  const loadingOrgs = ref(false)

  const isShopifyConnected = computed(() => {
    return !!orgData.value?.keys?.shopify?.isConnected
  })

  const searchUsagePercentage = computed(() => {
    return Math.min((searchCount.value / searchLimit.value) * 100, 100)
  })

  const fetchSearchCount = async () => {
    if (!orgData.value?.id) return

    try {
      const now = new Date()
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      
      const searchesRef = collection(db, 'searches')
      const q = query(
        searchesRef,
        where('org_id', '==', orgData.value.id),
        where('created_at', '>=', Timestamp.fromDate(firstDayOfMonth))
      )

      const querySnapshot = await getDocs(q)
      let count = 0
      querySnapshot.forEach(() => {
        count++
      })
      searchCount.value = count
    } catch (err: any) {
      console.error('Error fetching search count:', err)
      error.value = err.message
      searchCount.value = 0 // Reset count on error
    }
  }

  const watchOrgData = async () => {
    const authStore = useAuthStore()
    if (!authStore.user?.uid) return

    try {
      // First get the user's org_id
      const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
      const userData = userDoc.data()
      
      if (userData?.org_id) {
        // Cleanup existing subscription if any
        if (unsubscribe.value) {
          unsubscribe.value()
          unsubscribe.value = null
        }

        // Setup live updates for org data
        const orgRef = doc(db, 'config', userData.org_id)
        unsubscribe.value = onSnapshot(orgRef, (doc) => {
          if (doc.exists()) {
            orgData.value = {
              id: doc.id,
              share_code: doc.data().share_code,
              ...doc.data()
            } as OrgData
          }
        }, (err) => {
          error.value = err.message
          console.error('Error watching org data:', err)
        })
      }
    } catch (err: any) {
      error.value = err.message
      console.error('Error setting up org data watch:', err)
    }
  }

  const fetchOrgData = async () => {
    const authStore = useAuthStore()
    if (!authStore.user?.uid) return

    loading.value = true
    error.value = null

    try {
      // First get the user's org_id
      const userDoc = await getDoc(doc(db, 'users', authStore.user.uid))
      const userData = userDoc.data()
      
      if (userData?.org_id) {
        // Then get the org data from the config collection
        const orgDoc = await getDoc(doc(db, 'config', userData.org_id))
        if (orgDoc.exists()) {
          orgData.value = {
            id: orgDoc.id,
            share_code: orgDoc.data().share_code,
            ...orgDoc.data()
          } as OrgData
        }
      }
    } catch (err: any) {
      error.value = err.message
      console.error('Error fetching org data:', err)
    } finally {
      loading.value = false
    }
  }

  const cleanup = () => {
    if (unsubscribe.value) {
      unsubscribe.value()
      unsubscribe.value = null
    }
  }

  const updateWidgetConfig = async (widgetConfig: WidgetConfig) => {
    if (!orgData.value?.id) return

    loading.value = true
    error.value = null

    try {
      const orgRef = doc(db, 'config', orgData.value.id)
      await updateDoc(orgRef, {
        widget: widgetConfig
      })
    } catch (err: any) {
      error.value = err.message
      console.error('Error updating widget config:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchUserOrgs = async () => {
    const authStore = useAuthStore()
    if (!authStore.user?.uid) return

    loadingOrgs.value = true
    error.value = null

    try {
      // Call backend function to get user's orgs with names
      const getMyOrgsFn = httpsCallable(functions, 'getMyOrgs')
      const result = await getMyOrgsFn()
      const data = result.data as { success: boolean; orgs?: Array<{ org_id: string; org_name: string }> }

      if (data.success && data.orgs) {
        // Convert to OrgWithName format
        userOrgs.value = data.orgs.map((org) => ({
          user_id: authStore.user!.uid,
          org_id: org.org_id,
          org_name: org.org_name,
          updated_at: new Date()
        }))
      } else {
        userOrgs.value = []
      }
    } catch (err: any) {
      error.value = err.message
      console.error('Error fetching user orgs:', err)
      userOrgs.value = []
    } finally {
      loadingOrgs.value = false
    }
  }

  const switchOrg = async (orgId: string) => {
    loading.value = true
    error.value = null

    try {
      const switchOrgFn = httpsCallable(functions, 'switchOrg')
      const result = await switchOrgFn({ org_id: orgId })
      const data = result.data as { success: boolean; message?: string }

      if (!data.success) {
        throw new Error(data.message || 'Failed to switch organization')
      }

      // Refresh org data after switching
      await fetchOrgData()
      await watchOrgData()
      
      // Refresh other stores that depend on org_id
      // This will be handled by watching the user document for org_id changes
      
      return true
    } catch (err: any) {
      error.value = err.message
      console.error('Error switching org:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    orgData,
    loading,
    error,
    isShopifyConnected,
    searchCount,
    searchLimit,
    searchUsagePercentage,
    userOrgs,
    loadingOrgs,
    fetchOrgData,
    fetchSearchCount,
    watchOrgData,
    updateWidgetConfig,
    fetchUserOrgs,
    switchOrg,
    cleanup
  }
})